import { createContext, useState, type ReactNode, useCallback, useEffect } from 'react';
import type { AuthState, LoginCredentials, SignUpCredentials } from '@/types/auth';
import type { User } from '@/types/user';
import { authService } from '@/services/authService';
import { userService } from '@/services/userService';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

type ProviderLoginOptions = {
  onSuccess?: (data: { user: User }) => void;
  onError?: (error: unknown) => void;
};

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
};

export type AuthContextType = AuthState & {
  signIn: (credentials: LoginCredentials) => Promise<User>;
  signOut: () => Promise<void> | void;
  signUp: (credentials: SignUpCredentials) => Promise<void>;
  refreshToken: () => Promise<void>;
  loginWithProvider: (provider: string, code: string, options?: ProviderLoginOptions) => Promise<User>;
  fetchCurrentUser: () => void;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>(initialState);
  const { handleError } = useApiErrorHandler();

  const signIn = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);

      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: response.data,
      });
      return response.data;
    } catch (error) {
      handleError(error)
      // You might want to throw the error to be handled in the login form
      throw error;
    }
  };

  const signUp = async (credentials: SignUpCredentials) => {
    try {
      const response = await authService.register(credentials); // TODO: Create Sign Up flow
      console.log(response.data)

      // setAuthState({
      //   isAuthenticated: true,
      //   isLoading: false,
      //   user,
      // });
    } catch (error) {
      handleError(error)
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await authService.logout();
    } catch (error) {
      handleError(error)
    } finally {
      setAuthState({ ...initialState, isLoading: false });
    }
  };

  const refreshToken = async () => {
    try {
      await authService.refreshToken()
    } catch (err) {
      handleError(err)
      throw err
    }
  }

  const fetchCurrentUser = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await userService.fetchCurrentUser();

      setAuthState(prev => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false,
        user: response.data,
      }));
    } catch (err) {
      handleError(err)
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  }, []);

  const loginWithProvider = async (provider: string, code: string) => {
    try {
      const response = await authService.loginWithProvider(provider, code);

      // Update the global state
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: response.data,
      });

      // On success, resolve the promise by returning the data
      return response.data;

    } catch (err) {
      // Let the generic handler show a toast, etc.
      handleError(err);

      // On failure, reject the promise by re-throwing the error
      throw err;
    }
  };

  useEffect(() => {
    // Immediately try to get the current user when AuthProvider mounts
    const initAuth = async () => {
      try {
        await fetchCurrentUser();
      } catch (err) {
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: false,
          user: null,
        }));
      }
    };

    initAuth();
  }, [fetchCurrentUser]);



  const value: AuthContextType = {
    ...authState,
    signIn,
    signOut,
    signUp,
    refreshToken,
    fetchCurrentUser,
    loginWithProvider,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};